import React, { useEffect, useMemo, useState } from "react";

const ROLE_PRESENTATION = {
  user: {
    label: "Member",
    badge: "bg-[#EAF2FF] text-[#00ADEF]",
  },
  member: {
    label: "Member",
    badge: "bg-[#EAF2FF] text-[#00ADEF]",
  },
  merchant: {
    label: "Merchant",
    badge: "bg-[#FFF5E6] text-[#C27A00]",
  },
  fundraiser: {
    label: "Fundraiser",
    badge: "bg-[#E9EAEC] text-[#252A31]",
  },
};

const PermanentAccountDeletionModal = ({
  accounts = [],
  originAccountId,
  onCancel,
  onDelete,
  loading = false,
}) => {
  const [step, setStep] = useState("select");
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmationText, setConfirmationText] = useState("");
  const [pendingFundConfirmation, setPendingFundConfirmation] = useState(false);

  useEffect(() => {
    setSelectedIds(
      originAccountId && accounts.some((account) => account.id === originAccountId)
        ? [originAccountId]
        : [],
    );
    setStep(accounts.length > 1 ? "select" : "confirm");
    setConfirmationText("");
    setPendingFundConfirmation(false);
  }, [accounts, originAccountId]);

  const selectedAccounts = useMemo(
    () => accounts.filter((account) => selectedIds.includes(account.id)),
    [accounts, selectedIds],
  );

  const toggleAccount = (id) => {
    setPendingFundConfirmation(false);
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  };

  const selectAll = () => {
    setPendingFundConfirmation(false);
    setSelectedIds(
      selectedIds.length === accounts.length ? [] : accounts.map((item) => item.id),
    );
  };

  const showConfirmation = () => {
    if (!selectedAccounts.length) return;
    setConfirmationText("");
    setPendingFundConfirmation(false);
    setStep("confirm");
  };

  const confirmDeletion = () => {
    if (confirmationText !== "DELETE" || loading) return;
    const hasPendingFundraiserFunds = selectedAccounts.some(
      (account) =>
        account.role === "fundraiser" &&
        Number(account.pendingFundraiserFunds) > 0,
    );
    if (hasPendingFundraiserFunds) {
      setPendingFundConfirmation(true);
      return;
    }
    onDelete(selectedAccounts.map((account) => account.id), false);
  };

  const confirmPendingFundDeletion = () => {
    if (confirmationText !== "DELETE" || loading) return;
    onDelete(selectedAccounts.map((account) => account.id), true);
  };

  if (!accounts.length) return null;

  const isSingleAccount = selectedAccounts.length === 1;
  const singleAccount = selectedAccounts[0];
  const singleRole = singleAccount
    ? ROLE_PRESENTATION[singleAccount.role] || {
        label: singleAccount.roleLabel || singleAccount.role,
        badge: "bg-[#E9EAEC] text-[#252A31]",
      }
    : null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-[#15171B]/50 px-4 py-6">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="permanent-delete-title"
        className="relative w-full max-w-[512px] rounded-[22px] bg-white px-6 py-6 shadow-2xl sm:px-[25px]"
      >
        <button
          type="button"
          aria-label="Close deletion dialog"
          disabled={loading}
          onClick={onCancel}
          className="absolute right-5 top-5 text-[26px] leading-none text-[#687080] disabled:opacity-50"
        >
          ×
        </button>

        <div className="mb-3 flex h-[46px] w-[46px] items-center justify-center rounded-full bg-[#FFF0EC] text-[#E33A16]">
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
          >
            <path d="M4 7h16M9 7V4h6v3m-9 0 1 13h10l1-13M10 11v5m4-5v5" />
          </svg>
        </div>

        <h2
          id="permanent-delete-title"
          className="pr-8 text-[20px] font-[700] leading-tight text-[#20232A]"
        >
          {step === "select"
            ? "Delete account permanently"
            : isSingleAccount
              ? `Delete this ${singleRole?.label.toLowerCase()} and everything under it?`
              : `Delete ${selectedAccounts.length} accounts permanently?`}
        </h2>

        {pendingFundConfirmation ? (
          <>
            <p className="mt-4 text-[13px] leading-[1.5] text-[#20232A]">
              This fundraiser has commission still within the 30-day hold.
              If you continue, all unpaid fundraiser commission—including the
              amount currently pending release—will be retained by Swoodle and
              will not be paid to the fundraiser.
            </p>
            <div className="mt-4 rounded-[12px] bg-[#FFF5E6] px-3 py-3 text-[12px] font-[600] text-[#7A4B00]">
              Pending commission:{" "}
              {new Intl.NumberFormat("en-AU", {
                style: "currency",
                currency: "AUD",
              }).format(
                selectedAccounts.reduce(
                  (total, account) =>
                    total +
                    (account.role === "fundraiser"
                      ? Number(account.pendingFundraiserFunds) || 0
                      : 0),
                  0,
                ) / 100,
              )}
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingFundConfirmation(false)}
                disabled={loading}
                className="h-[44px] rounded-[12px] border border-[#E1E5EA] px-5 text-[13px] font-[600] text-[#20232A] disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={confirmPendingFundDeletion}
                disabled={loading}
                className="h-[44px] rounded-[12px] bg-[#E33A16] px-5 text-[13px] font-[600] text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Deleting..." : "Confirm deletion"}
              </button>
            </div>
          </>
        ) : step === "select" ? (
          <>
            <p className="mt-3 break-words text-[13px] leading-[1.5] text-[#687080]">
              {accounts[0]?.email} is linked to {accounts.length} account types.
              Choose which ones to permanently delete.
            </p>
            <div className="mt-6 flex items-center justify-between text-[13px] font-[600] text-[#20232A]">
              <span>Linked accounts</span>
              <button
                type="button"
                onClick={selectAll}
                className="font-[500] text-[#00ADEF]"
              >
                {selectedIds.length === accounts.length ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {accounts.map((account) => {
                const role = ROLE_PRESENTATION[account.role] || {
                  label: account.roleLabel || account.role,
                  badge: "bg-[#E9EAEC] text-[#252A31]",
                };
                const checked = selectedIds.includes(account.id);
                return (
                  <button
                    key={account.id}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    onClick={() => toggleAccount(account.id)}
                    className={`flex w-full items-center gap-3 rounded-[13px] border px-3 py-3 text-left ${
                      checked
                        ? "border-[#00ADEF] bg-[#EAF5FF]"
                        : "border-[#E1E5EA] bg-white"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border ${
                        checked
                          ? "border-[#00ADEF] bg-[#00ADEF] text-white"
                          : "border-[#A8AFBA] bg-white"
                      }`}
                    >
                      {checked && (
                        <svg
                          viewBox="0 0 16 16"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path d="m3 8 3.2 3.2L13 4.5" />
                        </svg>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-[600] text-[#20232A]">
                        {role.label} account
                      </span>
                      <span className="mt-0.5 block truncate text-[11px] text-[#687080]">
                        {account.summary || account.name || account.email}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-[500] ${role.badge}`}
                    >
                      {role.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex gap-2.5 rounded-[12px] bg-[#FFF0EC] px-3 py-3 text-[11px] leading-[1.5] text-[#20232A]">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#E33A16] text-[10px] font-[700] text-[#E33A16]">
                !
              </span>
              <p>
                This can&apos;t be undone. Selected accounts and their data will
                be permanently removed. Unselected linked accounts will remain.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="h-[44px] rounded-[12px] border border-[#E1E5EA] px-5 text-[13px] font-[600] text-[#20232A]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={showConfirmation}
                disabled={!selectedAccounts.length || loading}
                className="h-[44px] rounded-[12px] bg-[#E33A16] px-5 text-[13px] font-[600] text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete {selectedAccounts.length}{" "}
                {selectedAccounts.length === 1 ? "account" : "accounts"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-[#687080]">
              {isSingleAccount && (
                <span
                  className={`rounded-full px-2.5 py-1 text-[11px] font-[500] ${singleRole.badge}`}
                >
                  {singleRole.label}
                </span>
              )}
              <span className="break-all">
                {isSingleAccount
                  ? `${singleAccount.name || `${singleRole.label} account`} · ${singleAccount.email}`
                  : `${selectedAccounts.length} selected accounts · ${selectedAccounts[0]?.email}`}
              </span>
            </div>
            <p className="mt-4 text-[13px] leading-[1.5] text-[#20232A]">
              Deleting these accounts will permanently remove the selected
              account data. Data needed by other accounts will be retained or
              unlinked where appropriate. This action cannot be undone.
            </p>
            <label
              htmlFor="permanent-delete-confirmation"
              className="mt-5 block text-[12px] text-[#20232A]"
            >
              Type <strong>DELETE</strong> to confirm
            </label>
            <input
              id="permanent-delete-confirmation"
              value={confirmationText}
              onChange={(event) => setConfirmationText(event.target.value)}
              autoComplete="off"
              className="mt-1.5 h-[44px] w-full rounded-[11px] border border-[#E1E5EA] px-3 text-[14px] outline-none focus:border-[#00ADEF]"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (accounts.length > 1) {
                    setConfirmationText("");
                    setStep("select");
                  } else {
                    onCancel();
                  }
                }}
                disabled={loading}
                className="h-[44px] rounded-[12px] border border-[#E1E5EA] px-5 text-[13px] font-[600] text-[#20232A] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeletion}
                disabled={confirmationText !== "DELETE" || loading}
                className="h-[44px] rounded-[12px] bg-[#E33A16] px-5 text-[13px] font-[600] text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Deleting..." : "Delete permanently"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default PermanentAccountDeletionModal;
